/**
 * Generator lozinki za ekran promjene lozinke.
 *
 * Zasto 12 znakova: sa skupom A-Z a-z 0-9 (62 znaka) deset znakova nosi 60 bita
 * entropije, sto je oko godinu dana pri 10 milijardi pokusaja u sekundi - a
 * toliko se postize obicnom grafickom karticom nad procurenim hashem. Dvanaest
 * nosi 71 bit, odnosno oko 5.100 godina. Lozinka se ionako kopira, ne pamti, pa
 * dva znaka vise ne kostaju nista.
 */

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS = '0123456789';
const ALPHABET = UPPER + LOWER + DIGITS;

export const DEFAULT_LENGTH = 12;
export const LENGTH_OPTIONS = [12, 16, 20];

/**
 * Nasumican indeks bez pristranosti.
 *
 * `byte % 62` bi bio pristran: 256 nije djeljiv s 62, pa bi prvih 8 znakova
 * abecede izlazilo cesce od ostalih. Zato se bacaju bajtovi iznad najveceg
 * visekratnika i ponavlja se izvlacenje.
 */
const randomIndex = (max) => {
    const limit = Math.floor(256 / max) * max;
    const buf = new Uint8Array(1);

    for (;;) {
        crypto.getRandomValues(buf);
        if (buf[0] < limit) return buf[0] % max;
    }
};

const hasEachClass = (value) =>
    /[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value);

/**
 * Lozinka koja sadrzi barem jedno veliko slovo, malo slovo i znamenku.
 *
 * Uvjet se postize ponovnim izvlacenjem cijele lozinke, a ne podmetanjem znaka
 * na fiksno mjesto: podmetanje bi znacilo da je npr. treci znak uvijek znamenka,
 * sto napadacu suzava prostor pretrage.
 *
 * Odbacivanje kosta malo: 87,6% nasumicnih nizova od 12 znakova ionako pokriva
 * sva tri razreda, pa se rijetko ponavlja. Sam uvjet spusta entropiju sa 71,5 na
 * 71,3 bita - zanemarivo naspram toga da lozinka moze ispasti sama mala slova.
 */
export const generatePassword = (length = DEFAULT_LENGTH) => {
    const size = Math.max(8, Math.min(64, Math.floor(length) || DEFAULT_LENGTH));

    for (let attempt = 0; attempt < 100; attempt += 1) {
        let out = '';
        for (let i = 0; i < size; i += 1) {
            out += ALPHABET[randomIndex(ALPHABET.length)];
        }
        if (hasEachClass(out)) return out;
    }

    // Ne bi se trebalo dogoditi; radije pukni nego vrati lozinku slabiju od
    // obecane.
    throw new Error('Password generation failed to satisfy character classes.');
};

/** Bita entropije - za prikaz korisniku, da vidi sto duljina znaci. */
export const entropyBits = (length) => Math.round(length * Math.log2(ALPHABET.length));

/**
 * Kopiranje u medjuspremnik.
 *
 * navigator.clipboard postoji samo u sigurnom kontekstu (https ili localhost),
 * pa postoji i stara varijanta - inace bi gumb tiho ne radio na svakom okruzenju
 * koje nije pod https.
 */
export const copyToClipboard = async (value) => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
            return true;
        }
    } catch {
        // pada na zamjenu ispod
    }

    try {
        const el = document.createElement('textarea');
        el.value = value;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(el);
        return ok;
    } catch {
        return false;
    }
};
